import { getJobCategoryBadge } from "@/components/booking/booking-card-utils";
import { BrandColors } from "@/constants/theme";
import { useDesktopSheetStyle } from "@/hooks/use-desktop-layout";
import driverService from "@/lib/services/drivers";
import { showToast } from "@/lib/toast";
import { usePendingOffersStore } from "@/stores/use-pending-offers-store";
import Ionicons from "@react-native-vector-icons/ionicons";
import * as Haptics from "expo-haptics";
import { useRouter, useSegments } from "expo-router";
import React, { useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Modal,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";

export function NewBookingOfferModal() {
  const router = useRouter();
  const segments = useSegments();
  const [Loading, setLoading] = useState(false);
  const offers = usePendingOffersStore((s) => s.offers);
  const removeOffer = usePendingOffersStore((s) => s.removeOffer);
  const currentOffer = usePendingOffersStore((s) => s.currentOffer());
  const desktopSheetStyle = useDesktopSheetStyle();

  const activeBookingIdOnScreen = useMemo(() => {
    if (segments[0] !== "driver-booking-response") {
      return null;
    }

    const id = segments[1];
    const parsed = Number(id);

    return Number.isFinite(parsed) ? parsed : null;
  }, [segments]);

  const visibleOffer = useMemo(() => {
    if (!currentOffer) {
      return null;
    }

    if (
      activeBookingIdOnScreen !== null &&
      currentOffer.booking_id === activeBookingIdOnScreen
    ) {
      return null;
    }

    return currentOffer;
  }, [activeBookingIdOnScreen, currentOffer]);

  useEffect(() => {
    if (visibleOffer) {
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Warning).catch(
        () => {},
      );
    }
  }, [visibleOffer]);

  if (!visibleOffer) {
    return null;
  }

  const jobCategoryBadge = getJobCategoryBadge(
    visibleOffer.job_category,
    visibleOffer.scheduled_at,
  );

  const handleAccept = () => {
    removeOffer(visibleOffer.offer_id);
    router.push({
      pathname: `/driver-booking-response/${visibleOffer.booking_id}` as any,
      params: { vehicleId: String(visibleOffer.vehicle_id ?? "") },
    });
  };

  const handleDismiss = async () => {
    try {
      setLoading(true);
      await driverService.rejectBooking(visibleOffer.booking_id);
      removeOffer(visibleOffer.offer_id);
    } catch (error: any) {
      showToast.error(
        error.response?.data?.message || "Failed to reject booking",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal visible transparent animationType="slide">
      <View style={styles.overlay}>
        <View style={[styles.card, desktopSheetStyle]}>
          <View style={styles.header}>
            <Ionicons name="car-sport" size={32} color={BrandColors.primary} />
            <Text style={styles.title}>
              {visibleOffer.priority
                ? "Priority Booking"
                : "New Booking Request"}
            </Text>
          </View>

          {visibleOffer.service_type ? (
            <Text style={styles.serviceType}>{visibleOffer.service_type}</Text>
          ) : null}

          {jobCategoryBadge ? (
            <View
              style={[
                styles.categoryBadge,
                { backgroundColor: jobCategoryBadge.backgroundColor },
              ]}
            >
              <Ionicons
                name={jobCategoryBadge.icon}
                size={18}
                color={jobCategoryBadge.color}
              />
              <View style={styles.categoryText}>
                <Text
                  style={[styles.categoryLabel, { color: jobCategoryBadge.color }]}
                >
                  {jobCategoryBadge.label}
                </Text>
                <Text
                  style={[
                    styles.categoryDescription,
                    { color: jobCategoryBadge.color },
                  ]}
                >
                  {jobCategoryBadge.description}
                </Text>
              </View>
            </View>
          ) : null}

          <Text style={styles.route}>
            {visibleOffer.from_address ?? "Pickup"} →{" "}
            {visibleOffer.to_address ?? "Dropoff"}
          </Text>

          {visibleOffer.scheduled_at ? (
            <Text style={styles.schedule}>
              Scheduled for {formatScheduledAt(visibleOffer.scheduled_at)}
            </Text>
          ) : null}

          {offers.length > 1 && (
            <Text style={styles.queue}>+{offers.length - 1} more in queue</Text>
          )}

          <TouchableOpacity
            style={styles.primaryButton}
            onPress={handleAccept}
            disabled={Loading}
            activeOpacity={0.7}
          >
            <Text style={styles.primaryButtonText}>View & Quote</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.secondaryButton}
            onPress={handleDismiss}
            disabled={Loading}
            activeOpacity={0.7}
          >
            {Loading && (
              <ActivityIndicator color={BrandColors.mediumGray} size="small" />
            )}
            <Text style={styles.secondaryButtonText}>Dismiss</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
}

function formatScheduledAt(value: string): string {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return `${date.toLocaleDateString()} at ${date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.6)",
    justifyContent: "flex-end",
  },
  card: {
    backgroundColor: "#fff",
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    padding: 24,
    paddingBottom: 40,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginBottom: 16,
  },
  title: {
    fontSize: 20,
    fontWeight: "700",
    flex: 1,
  },
  serviceType: {
    fontSize: 16,
    fontWeight: "600",
    color: "#222",
    marginBottom: 8,
  },
  categoryBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 12,
  },
  categoryText: {
    flex: 1,
    gap: 2,
  },
  categoryLabel: {
    fontSize: 15,
    fontWeight: "700",
  },
  categoryDescription: {
    fontSize: 13,
  },
  route: {
    fontSize: 16,
    color: "#444",
    marginBottom: 8,
  },
  schedule: {
    fontSize: 15,
    color: "#444",
    marginBottom: 8,
  },
  queue: {
    fontSize: 14,
    color: "#888",
    marginBottom: 16,
  },
  primaryButton: {
    backgroundColor: BrandColors.primary,
    padding: 16,
    borderRadius: 12,
    alignItems: "center",
    marginTop: 8,
  },
  primaryButtonText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "600",
  },
  secondaryButton: {
    padding: 12,
    alignItems: "center",
    marginTop: 8,
    borderWidth: 1,
    borderColor: BrandColors.lightGray,
    borderRadius: 12,
    flexDirection: "row",
    justifyContent: "center",
    gap: 8,
  },
  secondaryButtonText: {
    color: "#666",
    fontSize: 16,
  },
});
